import React, { useState, useRef, useEffect } from 'react';
import { MessageSquare, Send } from 'lucide-react';
import { getBotReply } from '../../../utils/helperFunctions';
import { getUserTimezone } from '../../../utils/timezoneUtils';
import { options } from '../../../utils/helperFunctions';

interface Message {
  id: number;
  sender: 'user' | 'bot';
  text: string;
  time: string;
}

const COMMON_TOPICS = [
  'Device setup help',
  'WiFi connectivity',
  'Alert configuration',
  'Data export',
  'Account access',
];

const SUPPORT_HOURS = [
  { channel: 'Live Chat',  hours: 'Sun–Thu, 9AM–6PM GST', response: '< 5 minutes',    responseClass: 'hs-chat-response-fast' },
  { channel: 'Email',      hours: '24/7 (monitored)',      response: 'Within 24 hours', responseClass: 'hs-chat-response-slow' },
  { channel: 'Phone',      hours: 'Sun–Thu, 9AM–6PM GST', response: 'Immediate',       responseClass: 'hs-chat-response-fast' },
  { channel: 'WhatsApp',   hours: 'Sun–Thu, 8AM–8PM GST', response: 'Within 2 hours',  responseClass: 'hs-chat-response-med'  },
];

const ESCALATION = [
  { label: 'Chat Support',  desc: 'Immediate help for common issues' },
  { label: 'Email Ticket',  desc: 'Tracked resolution within 24h' },
  { label: 'Phone',         desc: 'Direct engineer for complex issues' },
  { label: 'On-site Visit', desc: 'For critical hardware failures' },
];

const WELCOME: Message = {
  id: 0,
  sender: 'bot',
  text: "👋 Hi! I'm a SmartEco support agent. How can I help you today? Select a topic or type your message.",
  time: '',
};

const ChatSupportTab: React.FC = () => {
  const userPreferences = JSON.parse(localStorage.getItem('userPreferences') || '{}');
  const timeZone = userPreferences.timezone || getUserTimezone();
  const now = () => new Date().toLocaleString('en-US', { timeZone, ...options });

  const [messages, setMessages] = useState<Message[]>([WELCOME]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  const sendMessage = (text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;

    setMessages((prev) => [
      ...prev,
      { id: prev.length + 1, sender: 'user', text: trimmed, time: now() },
    ]);
    setInput('');
    setIsTyping(true);

    setTimeout(() => {
      setIsTyping(false);
      setMessages((prev) => [
        ...prev,
        { id: prev.length + 1, sender: 'bot', text: getBotReply(trimmed), time: now() },
      ]);
    }, 1000);
  };

  return (
    <div className="hs-chat-layout">

      {/* Left — chat panel */}
      <div className="hs-chat-panel">

        {/* Header */}
        <div className="hs-chat-panel-header">
          <div className="na-section-label" style={{ margin: 0 }}>LIVE CHAT SUPPORT</div>
          <span className="hs-chat-online-badge"><span className="hs-chat-online-dot" />ONLINE</span>
        </div>

        {/* Info banner */}
        <div className="hs-chat-info-banner">
          <MessageSquare size={16} style={{ flexShrink: 0, color: 'var(--color-sub-title-text)' }} />
          <p>Chat with our support team for configuration help, technical issues, or device questions. Typical wait: &lt;5 minutes.</p>
        </div>

        {/* Messages */}
        <div className="hs-chat-messages scrollbar">
          {messages.map((msg) => (
            <div key={msg.id} className={`hs-chat-msg-row ${msg.sender === 'user' ? 'hs-chat-msg-user' : 'hs-chat-msg-bot'}`}>
              <div className="hs-chat-bubble">{msg.text}</div>
              {msg.time && <span className="hs-msg-time">{msg.time}</span>}
            </div>
          ))}
          {isTyping && (
            <div className="hs-chat-msg-row hs-chat-msg-bot">
              <div className="hs-msg-bubble hs-typing-indicator"><span /><span /><span /></div>
            </div>
          )}
          <div ref={endRef} />
        </div>

        {/* Common topics */}
        <div className="hs-chat-topics">
          <span className="hs-chat-topics-label">Common Topics:</span>
          <div className="hs-chat-topics-chips">
            {COMMON_TOPICS.map((t) => (
              <button key={t} className="hs-chat-chip" onClick={() => sendMessage(t)}>{t}</button>
            ))}
          </div>
        </div>

        {/* Input */}
        <div className="hs-chat-input-row">
          <input
            className="hs-chat-input"
            placeholder="Type your message..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') sendMessage(input); }}
          />
          <button className="hs-chat-send-btn" onClick={() => sendMessage(input)}>
            <Send size={16} />
            Send
          </button>
        </div>
      </div>

      {/* Right — sidebar */}
      <div className="hs-chat-sidebar">

        {/* Support Hours */}
        <div className="hs-chat-sidebar-card">
          <p className="na-section-label">SUPPORT HOURS</p>
          <table className="hs-chat-hours-table">
            <thead>
              <tr>
                <th>Channel</th>
                <th>Hours</th>
                <th>Response Time</th>
              </tr>
            </thead>
            <tbody>
              {SUPPORT_HOURS.map((row) => (
                <tr key={row.channel}>
                  <td>{row.channel}</td>
                  <td>{row.hours}</td>
                  <td className={row.responseClass}>{row.response}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Escalation Path */}
        <div className="hs-chat-sidebar-card">
          <p className="na-section-label" style={{ margin: 0 }}>ESCALATION PATH</p>
          <div className="hs-chat-escalation">
            {ESCALATION.map((item, i) => (
              <div key={i} className="hs-chat-escalation-item">
                <div className="hs-chat-escalation-num">{i + 1}</div>
                <p><strong>{item.label}–</strong> {item.desc}</p>
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
};

export default ChatSupportTab;
